import clsx from 'clsx';
import { Loader2 } from 'lucide-react';
// Types
import type {
  ButtonProps,
  ButtonSize,
  ButtonVariant,
} from '@/shared/components/Buttons/Button/Button.types';

const variantStyles: Record<ButtonVariant, string> = {
  primary: 'bg-primary text-white shadow-blue-200 hover:bg-primary/80',
  secondary: 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50',
  link: 'text-blue-600 hover:text-blue-700 hover:underline',
  destructive: 'bg-red-600 text-white hover:bg-red-700',
  outline: 'border border-blue-600 text-blue-600 hover:bg-blue-50',
  ghost: 'text-gray-700 hover:bg-gray-100',
  icon: 'text-gray-700 hover:bg-gray-100',
  warning: 'bg-amber-500 text-white hover:bg-amber-600',
};

const sizeStyles: Record<ButtonSize, string> = {
  small: 'px-3 py-1.5 text-sm',
  medium: 'px-4 py-2',
  large: 'px-6 py-3 text-lg',
  icon: 'p-2',
};

export function Button({
  variant = 'primary',
  size = 'medium',
  type = 'button',
  isLoading = false,
  leftIcon,
  rightIcon,
  children,
  className,
  disabled,
  ...props
}: ButtonProps) {
  const isDisabled = (disabled ?? false) || isLoading;

  return (
    <button
      type={type}
      disabled={isDisabled}
      className={clsx(
        'flex cursor-pointer items-center justify-center gap-2 rounded-xl font-medium transition-colors',
        variantStyles[variant],
        sizeStyles[size],
        isDisabled && 'cursor-not-allowed opacity-50',
        className
      )}
      {...props}
    >
      {isLoading ? <Loader2 className='h-4 w-4 animate-spin' /> : leftIcon}
      {children}
      {!isLoading && rightIcon}
    </button>
  );
}
